import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import type { Column, RowRenderProps, SortingRule } from 'react-table';
import { createStructuredSelector } from 'reselect';
import { Action } from '../../../../../typings/permissions';
import {
  userHasPermissionForTeamProjects,
  userHasPermissionForTeamReleaseGroups,
} from '../../../../helpers/permissions';
import type { ReduxStore } from '../../../../redux/store';
import type { TeamsStore } from '../../../../redux/teams';
import TeamsModule from '../../../../redux/teams';
import type { Organization, Team } from '../../../../types';
import Loader from '../../../generic/Loader';
import PopperMenu from '../../../generic/PopperMenu';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import { useDialogContext } from '../../../../contexts/DialogContext';

type OwnProps = {
  team: Team;
  search: string;
  organization: Organization;
  releaseGroups: Array<{ id: number; title: string }>;
};

type ReduxStateProps = {
  pendingRemovedProjects: TeamsStore['pendingRemovedProjects'];
  pendingRemovedReleaseGroups: TeamsStore['pendingRemovedReleaseGroups'];
};

type DispatchProps = {
  removeProjects(teamId: number, projectIds: string[]): void;
  removeReleaseGroups(teamId: number, groupIds: number[]): void;
};

type TeamProjectsProps = OwnProps & ReduxStateProps & DispatchProps;

type TeamDataRow =
  | {
      projectId: string;
      title: string;
      type: 'project';
    }
  | {
      releaseGroupId: number;
      title: string;
      type: 'releaseGroup';
    };

const prepareProjectData = (
  teamProjects: Team['teamProjects'],
  projects: Array<{ locator: string; title: string }>,
  teamReleaseGroups: Team['teamReleaseGroups'],
  releaseGroups: Array<{ id: number; title: string }>,
  search: string,
  sortMode: SortingRule,
): TeamDataRow[] => {
  const projectRows =
    teamProjects?.map(
      ({ projectId }): TeamDataRow => ({
        projectId,
        title: projects.find((project) => project.locator === projectId)?.title || 'Unknown',
        type: 'project',
      }),
    ) || [];

  let releaseGroupRows = [];
  if (teamReleaseGroups && releaseGroups) {
    releaseGroupRows = teamReleaseGroups.map(
      ({ releaseGroupId }): TeamDataRow => ({
        releaseGroupId,
        title: releaseGroups.find((releaseGroup) => releaseGroup.id === releaseGroupId)?.title || 'Unknown',
        type: 'releaseGroup',
      }),
    );
  }

  let rows = [...projectRows, ...releaseGroupRows];

  if (search) {
    const searchLower = search.toLowerCase();
    rows = rows.filter((row) => row.title.toLowerCase().includes(searchLower));
  }

  const { id, desc: isDescending } = sortMode;
  return rows.sort((a, b) => {
    if (isDescending) {
      return b[id].toLowerCase().localeCompare(a[id].toLowerCase());
    }
    return a[id].toLowerCase().localeCompare(b[id].toLowerCase());
  });
};

const TeamProjects = ({
  team,
  search,
  organization,
  pendingRemovedProjects,
  removeProjects,
  releaseGroups,
  removeReleaseGroups,
  pendingRemovedReleaseGroups,
}: TeamProjectsProps) => {
  const [sortMode, setSortMode] = useState<SortingRule>({ id: 'title', desc: false });

  const [projectData, setProjectData] = useState<TeamDataRow[]>([]);
  const { onConfirm } = useDialogContext();

  useEffect(() => {
    setProjectData(
      prepareProjectData(
        team.teamProjects,
        organization.projects,
        team.teamReleaseGroups,
        releaseGroups,
        search,
        sortMode,
      ),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: check that this is correct.
  }, [team, organization.projects, search, sortMode]);

  const canRemoveProjects = userHasPermissionForTeamProjects(Action.Remove, team.id);

  const canRemoveReleaseGroups = userHasPermissionForTeamReleaseGroups(Action.Remove, team.id);

  //  Returns the click handler for removing projects/releaseGroups. Displays confirm modal first.
  const onRemoveClick =
    ({ isProject, id, title }: { isProject: boolean; id: string | number; title: string }) =>
      () =>
        onConfirm({
          title: `Are you sure you want to remove "${title}"?`,
          body: `These ${isProject ? 'project(s)' : 'release group(s)'} will no longer be available to this team.`,
          onConfirm: () =>
            isProject ? removeProjects(team.id, [id as string]) : removeReleaseGroups(team.id, [id as number]),
          confirmText: 'Remove',
          confirmBtnType: 'error',
        });

  const releaseGroupEditDropdownButton = (groupId: number, title: string) =>
    canRemoveReleaseGroups && (
      <PopperMenu>
        <PopperMenu.Item
          onClick={onRemoveClick({
            title,
            id: groupId,
            isProject: false,
          })}
          isWarning
        >
          Remove release group
        </PopperMenu.Item>
      </PopperMenu>
    );

  const projectEditDropdownButton = (projectId: string, title: string) =>
    canRemoveProjects && (
      <PopperMenu>
        <PopperMenu.Item
          onClick={onRemoveClick({
            title,
            id: projectId,
            isProject: true,
          })}
          isWarning
        >
          Remove project
        </PopperMenu.Item>
      </PopperMenu>
    );

  const renderEditButton = (row: TeamDataRow) => {
    if (row.type === 'releaseGroup') {
      if (pendingRemovedReleaseGroups && pendingRemovedReleaseGroups.includes(row.releaseGroupId)) {
        return <Loader size={30} style={{ height: '30px', width: '30px' }} />;
      }
      return releaseGroupEditDropdownButton(row.releaseGroupId, row.title);
    }
    if (pendingRemovedProjects && pendingRemovedProjects.includes(row.projectId)) {
      return <Loader size={30} style={{ height: '30px', width: '30px' }} />;
    }
    return projectEditDropdownButton(row.projectId, row.title);
  };

  const columns: Array<Column<any>> = [
    {
      id: 'title',
      accessor: 'title',
      Header: <TableHeader title="Name" sort={{ id: 'title', currentMode: sortMode }} />,
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: { original: TeamDataRow } & RowRenderProps) =>
        // eslint-disable-next-line react/jsx-no-useless-fragment -- Using a Fragment for typing.
        <>{original.title}</>,
      resizable: false,
    },
    {
      id: 'type',
      accessor: 'type',
      Header: <TableHeader title="Type" sort={{ id: 'type', currentMode: sortMode }} />,
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: { original: TeamDataRow } & RowRenderProps) => (
        <>
          {original.type === 'project' ? 'Project' : 'Release Group'}
          {renderEditButton(original)}
        </>
      ),
      width: 250,
      style: {
        overflow: 'visible',
        paddingRight: '15px',
      },
    },
  ];

  return (
    <div className="team-settings__team-projects">
      <SimpleReactTable
        data={projectData}
        columns={columns}
        defaultSortMode={[
          {
            id: 'title',
            desc: false,
          },
        ]}
        onSortedChanged={setSortMode}
        showPagination={projectData.length > 20}
        cacheId="teamProjects"
      />
    </div>
  );
};

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  pendingRemovedProjects: TeamsModule.selectors.getPendingRemovedProjects,
  pendingRemovedReleaseGroups: TeamsModule.selectors.getPendingRemovedReleaseGroups,
});

const mapDispatchToProps: DispatchProps = {
  removeProjects: TeamsModule.actions.removeTeamProjects,
  removeReleaseGroups: TeamsModule.actions.removeTeamReleaseGroups,
};

export default connect(mapStateToProps, mapDispatchToProps)(TeamProjects);
